import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router';
import { fileFacts } from '../../core/file-facts';
import { formatOfContentType } from '../../core/materials';
import { domSaver, type MaterialSummary, type ObjectUrls } from '../api';
import { ACCEPTED_FILE_TYPES, STUDENT_RECORDS_NOTICE } from '../upload-copy';
import FileIcon from './FileIcon';
import UndoNotice from './UndoNotice';

/**
 * What the group has shared (R12).
 *
 * Three states, all of them ordinary: a group with nothing in it yet is the first thing
 * every new group is, a slow network is the second, and a failed fetch is the third. None of
 * these is an edge case, and a list that renders only the happy path renders a lie for the
 * other two.
 *
 * The R15 sentence is repeated here, where materials are listed, per the Key Decision: the
 * rule is stated where a teacher is deciding what to share, not only at the moment they pick
 * a file.
 *
 * **Adding one is a link, to `/g/:groupId/new`.** The form used to sit above this list, which
 * put "share something" at the top of a screen whose subject is what has already been shared.
 * It is a destination now, so this list is the room's main content and the way to add to it is
 * one primary control — offered twice, in the header and in the empty state, because a group
 * with nothing in it has nothing else to offer.
 *
 * Opening a material is a **link** too, not a button that swaps state underneath this list.
 * Its page has an address of its own, so a teacher can middle-click it, copy it, send it to a
 * colleague in the same group, and come back with the browser's own back button. None of that
 * is true of a panel a click grows at the bottom of the screen.
 *
 * A card says four things under the title — who shared it, when, how big, what kind — because
 * "whose is this and is it the one from Tuesday" is the question a teacher opens this list
 * with. `sharedBy` is resolved by the caller against the group's own roster; this component
 * never looks anyone up, exactly as the comment thread never does (R18).
 *
 * **Delete is one click, with Undo after it**. A deleted material sits in the trash for
 * thirty days, so the click asks nothing; the notice it leaves behind is the way back, and the
 * room's "Recently deleted" section is the way back after the notice has gone.
 *
 * **A grid by default, and the list a click away**. A card leads with the material's first
 * page or picture — what a teacher recognises before they have read a word — then its title,
 * its facts ("Slides · 12 slides · 2.4 MB"), who shared it and when, and the same actions as a
 * row. The list is today's markup, unchanged but for the same facts line. The choice is kept in
 * `localStorage` under `collab:materials-view`, inside try/catch: a browser that refuses
 * storage gets the grid every time and nothing breaks.
 *
 * A thumbnail is fetched with the bearer token like every other read (`fetchThumbnail`), so it
 * reaches the `<img>` as an object URL this component creates and revokes. It is asked for only
 * when the listing says one exists, only once the card is near the screen, and on any failure
 * the card shows its file-type icon instead.
 *
 * `collab-material-list` is a replay block selector (`analytics/client.ts`). Titles, notes
 * and filenames are what teachers wrote; none of them belongs in a recording.
 */

export type FetchState = 'loading' | 'ready' | 'failed';

/** The material just deleted, while its Undo is on offer. */
export interface UndoDelete {
  /** Changes with each delete, so a second delete restarts the clock. */
  id: string;
  title: string;
  busy: boolean;
  onUndo: () => void;
  onExpire: () => void;
}

export interface MaterialListProps {
  materials: MaterialSummary[];
  state: FetchState;
  /** True when this teacher may delete this material — their own, or any if they are admin. */
  canDelete: (material: MaterialSummary) => boolean;
  /** Where this material's own page lives. Built by `src/routes.ts`; never carries a token. */
  linkTo: (material: MaterialSummary) => string;
  /** Where adding one lives — `/g/:groupId/new`. A destination, never a form in this panel. */
  addHref: string;
  /** Several files at once, into the upload panel. Absent where no queue is mounted. */
  onAddFiles?: (files: File[]) => void;
  /** "You", a member's address, or "Former member". Resolved by the caller, never here. */
  sharedBy: (material: MaterialSummary) => string;
  onDownload: (material: MaterialSummary) => void;
  onDelete: (material: MaterialSummary) => void;
  onRetry: () => void;
  downloadError: string | null;
  undo?: UndoDelete | null;
  /** A delete or an undo that did not go through. */
  deleteError?: string | null;
  /** A material's thumbnail, or null. Absent, every card shows its icon. */
  loadThumbnail?: (material: MaterialSummary) => Promise<Blob | null>;
  /** Injected in tests. The DOM's own in the browser. */
  objectUrls?: ObjectUrls;
  /** False when the unread kill switch is on. Markers show by default (R11). */
  showUnread?: boolean;
}

/** Where the grid-or-list choice is kept. */
export const MATERIALS_VIEW_KEY = 'collab:materials-view';

export type MaterialsView = 'grid' | 'list';

const readView = (): MaterialsView => {
  try {
    return window.localStorage.getItem(MATERIALS_VIEW_KEY) === 'list' ? 'list' : 'grid';
  } catch {
    return 'grid';
  }
};

const saveView = (view: MaterialsView): void => {
  try {
    window.localStorage.setItem(MATERIALS_VIEW_KEY, view);
  } catch {
    // Private mode, or storage refused. The choice lasts this visit, which is enough.
  }
};

/** What a teacher calls the file, not what the wire calls it. */
const TYPE_LABEL: Readonly<Record<string, string>> = {
  pdf: 'PDF', docx: 'Word', pptx: 'PowerPoint', png: 'PNG', jpeg: 'JPEG', text: 'Text',
};

export const typeLabel = (contentType: string): string => {
  const format = formatOfContentType(contentType);
  return format === null ? 'File' : TYPE_LABEL[format] ?? 'File';
};

/**
 * A date, in the reader's own locale.
 *
 * Both of these take the ISO string the Worker wrote and hand back something a teacher reads.
 * The `<time>` elements that carry them keep the ISO string in `dateTime`, so the exact
 * instant is still there for anything that needs it — and so a test can assert the value
 * rather than a locale's rendering of it.
 */
export const whenLabel = (iso: string): string => {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  return at.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

/** The same, with the time of day — for a conversation, where the hour is the point. */
export const whenExactly = (iso: string): string => {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  return at.toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

/** Two buttons, one pressed. A segmented control, not a toggle that hides what it toggles to. */
function ViewToggle({ view, onChange }: { view: MaterialsView; onChange: (view: MaterialsView) => void }) {
  return (
    <div className="view-toggle" role="group" aria-label="Show materials as">
      {(['grid', 'list'] as const).map((option) => (
        <button
          key={option}
          type="button"
          className="view-toggle-option"
          aria-pressed={view === option}
          onClick={() => onChange(option)}
        >
          {option === 'grid' ? 'Grid' : 'List'}
        </button>
      ))}
    </div>
  );
}

/**
 * A card's picture: the thumbnail when there is one and it loads, the icon otherwise.
 *
 * Lazy: nothing is fetched until the card is within a screen of the viewport, where the
 * browser can say so. `load` and `urls` are read through a ref, because the room hands in a
 * fresh closure every render and a fetch per render is not lazy.
 */
function Thumb({
  material, load, urls,
}: {
  material: MaterialSummary;
  load: ((material: MaterialSummary) => Promise<Blob | null>) | undefined;
  urls: ObjectUrls;
}) {
  const box = useRef<HTMLSpanElement>(null);
  const deps = useRef({ load, urls });
  useEffect(() => { deps.current = { load, urls }; });
  const [near, setNear] = useState(() => typeof IntersectionObserver === 'undefined');
  const [src, setSrc] = useState<string | null>(null);
  const wanted = material.hasThumbnail === true && load !== undefined;

  useEffect(() => {
    if (near || !wanted || !box.current) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setNear(true);
        observer.disconnect();
      }
    }, { rootMargin: '200px' });
    observer.observe(box.current);
    return () => observer.disconnect();
  }, [near, wanted]);

  useEffect(() => {
    const fetchIt = deps.current.load;
    if (!near || !wanted || !fetchIt) return undefined;
    const owner = deps.current.urls;
    let cancelled = false;
    let made: string | null = null;
    fetchIt(material).then((blob) => {
      if (cancelled || !blob) return;
      try {
        made = owner.createObjectURL(blob);
        setSrc(made);
      } catch {
        // No object URLs here (jsdom, or a browser refusing them): the icon stays.
      }
    }, () => { /* the icon stays */ });
    return () => {
      cancelled = true;
      if (made) owner.revokeObjectURL(made);
      setSrc(null);
    };
    // The material's id and its flag are what a fetch is for; see the ref above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [near, wanted, material.id]);

  const kind = formatOfContentType(material.contentType);
  return (
    <span ref={box} className={`material-thumb${kind === 'png' || kind === 'jpeg' ? ' is-picture' : ''}`}>
      {src ? (
        <img
          className="material-thumb-image"
          src={src}
          alt=""
          decoding="async"
          onError={() => {
            deps.current.urls.revokeObjectURL(src);
            setSrc(null);
          }}
        />
      ) : (
        <FileIcon contentType={material.contentType} size="lg" />
      )}
    </span>
  );
}

function AddFiles({ onAddFiles }: { onAddFiles: (files: File[]) => void }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <button type="button" className="secondary" onClick={() => input.current?.click()}>
        Add files
      </button>
      <input
        ref={input}
        type="file"
        multiple
        hidden
        aria-label="Choose materials to add"
        accept={ACCEPTED_FILE_TYPES}
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          event.target.value = '';
          if (files.length > 0) onAddFiles(files);
        }}
      />
    </>
  );
}

/** Initials from the label already available locally; no profile lookup. */
export const avatarInitials = (label: string): string => {
  const parts = (label.split('@')[0] ?? '').split(/[\s._-]+/).filter(Boolean);
  return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase() || '?';
};

export default function MaterialList({
  materials, state, canDelete, linkTo, addHref, sharedBy, onAddFiles,
  onDownload, onDelete, onRetry, downloadError, undo = null, deleteError = null,
  loadThumbnail, objectUrls = domSaver, showUnread = true,
}: MaterialListProps) {
  const [view, setView] = useState<MaterialsView>(readView);
  const choose = (next: MaterialsView) => { setView(next); saveView(next); };
  const listed = state === 'ready' && materials.length > 0;

  /** The three actions, the same on a card and on a row. */
  const actions = (material: MaterialSummary) => (
    <div className="row-actions">
      <button type="button" className="secondary" onClick={() => onDownload(material)}>
        Download
      </button>
      <Link className="secondary button-link" to={linkTo(material)}>
        Comments
      </Link>
      {canDelete(material) && (
        <button type="button" className="secondary danger" onClick={() => onDelete(material)}>
          Delete
        </button>
      )}
    </div>
  );

  /**
   * The title link, with the unread dot inside it (R6). The dot is drawn for the eye and the
   * words are for a screen reader, both inside the one link a card already announces, so an
   * unread card is still one link: "Ratios warm-up New comments".
   */
  const title = (material: MaterialSummary) => {
    const unread = showUnread && material.unread === true;
    return (
      <Link className="link material-open" to={linkTo(material)}>
        {unread && <span className="unread-dot" aria-hidden="true" />}
        {material.title}
        {unread && <span className="visually-hidden"> New comments</span>}
      </Link>
    );
  };

  const [query, setQuery] = useState('');
  const searchId = useId();
  const needle = query.trim().toLocaleLowerCase();
  const visible = materials.filter((material) =>
    [material.title, material.filename, material.note ?? '', typeLabel(material.contentType), sharedBy(material)]
      .some((value) => value.toLocaleLowerCase().includes(needle)));
  return (
    <section className="table-library collab-material-list" aria-label="Materials">
      <div className="table-library-head">
        <div><p className="table-eyebrow">The shared collection</p><h3>Materials</h3></div>
        <div className="row-actions">
          {listed && <ViewToggle view={view} onChange={choose} />}
          {/* A picker for several files at once; each gets its own row in the upload panel. */}
          {onAddFiles && <AddFiles onAddFiles={onAddFiles} />}
          {/* The room's one primary action. A link, because it goes somewhere. */}
          <Link className="button-link primary-link" to={addHref}>Add material</Link>
        </div>
      </div>

      {listed && (
        <div className="table-search">
          <label htmlFor={searchId}>Search materials</label>
          <input id={searchId} type="search" value={query} onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by title, file or colleague" />
        </div>
      )}
      <p className="notice">{STUDENT_RECORDS_NOTICE}</p>

      {downloadError && <p className="error" role="alert">{downloadError}</p>}

      {undo && (
        <UndoNotice
          key={undo.id}
          title={undo.title}
          busy={undo.busy}
          onUndo={undo.onUndo}
          onExpire={undo.onExpire}
        />
      )}

      {deleteError && <p className="error" role="alert">{deleteError}</p>}

      {state === 'loading' && <p className="hint" role="status">Loading materials…</p>}

      {state === 'failed' && (
        <div role="alert">
          <p className="error">We could not load this group&rsquo;s materials.</p>
          <button type="button" onClick={onRetry}>Try again</button>
        </div>
      )}

      {state === 'ready' && materials.length === 0 && (
        <div className="empty">
          <p className="hint" role="status">
            Nothing shared yet. The first material you share is what the group talks about.
          </p>
          <Link className="button-link primary-link" to={addHref}>Add material</Link>
        </div>
      )}

      {listed && (
        <p className="table-result-count" role="status">{visible.length} {visible.length === 1 ? 'material' : 'materials'}{needle ? ' found' : ' shared'}</p>
      )}
      {listed && visible.length === 0 && (
        <div className="empty"><p>No materials match your search.</p><button type="button" className="secondary" onClick={() => setQuery('')}>Clear search</button></div>
      )}

      {listed && visible.length > 0 && view === 'grid' && (
        <ul className="material-grid" aria-label="Materials">
          {visible.map((material) => (
            <li key={material.id} className="material-card">
              {/* The picture is a second way in for a pointer, and hidden from everything else:
                  the title below is the link, and one link per card is what a screen reader
                  should hear. */}
              <Link className="material-card-thumb" to={linkTo(material)} tabIndex={-1} aria-hidden="true">
                <Thumb material={material} load={loadThumbnail} urls={objectUrls} />
              </Link>
              <div className="material-card-body">
                {title(material)}
                <p className="meta material-card-facts">{fileFacts(material)}</p>
                <p className="meta material-card-by">
                  <span className="table-avatar" aria-hidden="true">{avatarInitials(sharedBy(material))}</span>
                  <span className="material-sharer">{sharedBy(material)}</span>
                  <span aria-hidden="true"> · </span>
                  <time dateTime={material.createdAt}>{whenLabel(material.createdAt)}</time>
                </p>
                <p className="meta material-filename">{material.filename}</p>
                {material.note && <p className="note material-card-note">{material.note}</p>}
              </div>
              <div className="material-card-actions">{actions(material)}</div>
            </li>
          ))}
        </ul>
      )}

      {listed && visible.length > 0 && view === 'list' && (
        <ul className="materials" aria-label="Materials">
          {visible.map((material) => (
            <li key={material.id}>
              <div className="material-body">
                {/*
                  * The title is the way in, because "open the thing I am looking at" is what
                  * a teacher means by clicking its name. The Comments link below does the
                  * same job for anyone who reads the row as a list of actions.
                  */}
                {title(material)}
                <p className="meta material-facts">
                  <span className="table-avatar" aria-hidden="true">{avatarInitials(sharedBy(material))}</span>
                  <span className="material-sharer">{sharedBy(material)}</span>
                  <span aria-hidden="true">·</span>
                  <time dateTime={material.createdAt}>{whenLabel(material.createdAt)}</time>
                  <span aria-hidden="true">·</span>
                  {/* Kind, pages where counted, and size — the card's facts line. */}
                  <span className="material-kind">{fileFacts(material)}</span>
                </p>
                <p className="meta material-filename">{material.filename}</p>
                {material.note && <p className="note">{material.note}</p>}
              </div>
              {actions(material)}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
